import './FetchFilterBar.scss';
import cn from 'classnames';
import * as R from 'ramda';
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { Form, Modal } from 'react-bootstrap';
import {
  MdAdd,
  MdCancel,
  MdCircle,
  MdClear,
  MdEdit,
  MdOutlineArrowBackIos,
  MdOutlineArrowForwardIos,
  MdOutlineCircle,
  MdSave
} from 'react-icons/md';
import {
  buildSqlBasicToAdvanced,
  filterTokenizer,
  tokensToWhereClause
} from '@blisspointmedia/bpm-types/dist/advancedFilterParser';
import { isInvalidAnalyticsDimension } from '@blisspointmedia/bpm-types/dist/Columns';
import {
  Category,
  FilterPaneAdvancedState,
  FilterPaneBasicState,
  FilterPaneState
} from '@blisspointmedia/bpm-types/dist/FilterPane';
import {
  DeleteFilterPresetParams,
  GetFilterPresetsParams,
  SaveFilterPresetBody
} from '@blisspointmedia/bpm-types/dist/FilterPresets';
import { FilterPreset } from '@blisspointmedia/bpm-types/dist/pgTables/FilterPreset';
import { GroupedSegment } from '@blisspointmedia/bpm-types/dist/SegmentationMapping';
import { getPayloadSize } from '../../Pages/CopyTiger/copyTigerUtils';
import { useAppSelector } from '../../redux';
import { useSetAreYouSure, useSetError } from '../../redux/modals';
import * as UserRedux from '../../redux/user';
import {
  getInvalidGranularityDetailsBySource,
  getSelectedFilterDims,
  InvalidGranularityDetails,
  makeInvalidDimMsg
} from '../../utils/columnMetricSystem/configUtils';
import { awaitJSON, FiltersLambdaFetch } from '../../utils/fetch-utils';
import { useExperimentalAnalyticsCols } from '../../utils/hooks/useExperimentalAnalyticsCols';
import { SingleChannelMediaTypes } from '../../utils/performance-utils';
import { AdminLockToggle } from '../AdminLockToggle/AdminLockToggle';
import { Button } from '../Button/Button';
import { Appearance, Background, Design, Intent, Placement, Size } from '../designConstants';
import { IconButton } from '../IconButton';
import { areFilterStatesEqual } from '../MetricsTable/metrics-table-utils';
import { PresetDropdown } from '../PresetDropdown/PresetDropdown';
import { RenameModal } from '../RenameModal/RenameModal';
import { SegmentedControlButton } from '../SegmentedControlButton';
import { buildSavePresetFilter, getFilterNameError } from './fetch-filter-bar-utils';
import { FetchFilterBarChip } from './FetchFilterBarChip';
import { FilterPaneAdvanced } from './FilterPaneAdvanced';
import { FilterPaneBasic } from './FilterPaneBasic';

const MAX_WHERE_CLAUSE_SIZE = 75 * 1000;
const EMPTY_COLS: string[] = [];

type CategoryWithValidity = Category & { isInvalid?: boolean; invalidMsg?: string };

export const sortCategories = (categories: Category[], state: FilterPaneState): Category[] => {
  const notMap = state?.basic?.notMap ?? {};
  const selectedMap = state?.basic?.selectedMap ?? {};

  const countSelected = (key: string): number => {
    let count = 0;
    for (const k of Object.keys(notMap[key] ?? {})) {
      if (notMap[key][k]) {
        count++;
      }
    }
    for (const k of Object.keys(selectedMap[key] ?? {})) {
      if (selectedMap[key][k]) {
        count++;
      }
    }
    return count;
  };

  return [...categories].sort((cat1, cat2) =>
    countSelected(cat1.key) > countSelected(cat2.key) ? -1 : 1
  );
};

export const DEFAULT_FILTER_PANE_STATE = {
  isAdvanced: false,
  basic: {
    notMap: {},
    selectedMap: {}
  },
  advanced: ''
};

export const useFilterPaneState = (
  init?: FilterPaneState
): [FilterPaneState, Dispatch<SetStateAction<FilterPaneState>>, () => void] => {
  const [state, setState] = useState<FilterPaneState>({ ...DEFAULT_FILTER_PANE_STATE, ...init });
  const resetState = useCallback(() => setState(DEFAULT_FILTER_PANE_STATE), []);
  return [state, setState, resetState];
};

export const hasFilter = (state: FilterPaneState): boolean => {
  if (state.isAdvanced) {
    return Boolean(state.advanced);
  }
  for (const category of R.keys(state.basic.selectedMap)) {
    for (const option of R.keys(state.basic.selectedMap[category])) {
      if (state.basic.selectedMap[category][option]) {
        return true;
      }
    }
  }
  return false;
};

interface FetchFilterBarProps {
  categories: Category[];
  groupedCategories?: Record<string, GroupedSegment[]>;
  className?: string;
  company?: string;
  disabled?: boolean;
  filterID?: number;
  filters: FilterPreset[] | undefined;
  filterSource: 'presetFilterID' | 'currentFilterID' | 'presetState' | 'currentState' | undefined;
  platform?: string;
  readonly?: boolean;
  refreshData?: () => void;
  setFilterID: Dispatch<SetStateAction<number | undefined>>;
  setFilters: Dispatch<SetStateAction<FilterPreset[] | undefined>>;
  setFilterSource: Dispatch<
    SetStateAction<'presetFilterID' | 'currentFilterID' | 'presetState' | 'currentState'>
  >;
  setState: Dispatch<SetStateAction<FilterPaneState>>;
  state: FilterPaneState;
  analyticsIsSource?: boolean;
  simpleFilterStateOnly?: boolean;
  incompatibleFilterDimsFromDelivery?: InvalidGranularityDetails;
  // current metrics table dims + data cols; filter dims are checked together with them
  tableCols?: string[];
  channel?: SingleChannelMediaTypes;
}

export const FetchFilterBar = ({
  categories = [],
  groupedCategories = {},
  className,
  company,
  disabled,
  filterID,
  filters,
  filterSource,
  platform,
  readonly,
  refreshData = () => {},
  setFilterID,
  setFilters,
  setFilterSource,
  setState,
  state,
  analyticsIsSource = false,
  simpleFilterStateOnly = false,
  incompatibleFilterDimsFromDelivery = {},
  tableCols = EMPTY_COLS,
  channel
}: FetchFilterBarProps) => {
  const [adminLock, setAdminLock] = useState<boolean>(false);
  const [advanced, setAdvanced] = useState<FilterPaneAdvancedState>(state.advanced);
  const [basic, setBasic] = useState<FilterPaneBasicState>(state.basic);
  const [compileError, setCompileError] = useState<string>('');
  const [expanded, setExpanded] = useState<boolean>(false);
  const [fetchingFilters, setFetchingFilters] = useState<boolean>(false);
  const [initialOpenSection, setInitialOpenSection] = useState<string>('');
  const [isAdvanced, setIsAdvanced] = useState<boolean>(state.isAdvanced);
  const [newFilterName, setNewFilterName] = useState<string | undefined>();
  const [renameFilterOnSave, setRenameFilterOnSave] = useState<boolean>(false);
  const [saveAsNew, setSaveAsNew] = useState<boolean>(false);
  const [selectedFilter, setSelectedFilter] = useState<FilterPreset | undefined>();
  const [showCreateFilterModal, setShowCreateFilterModal] = useState<boolean>(false);
  const [showEditFilterModal, setShowEditFilterModal] = useState<boolean>(false);
  const [showRenameFilterModal, setShowRenameFilterModal] = useState<boolean>(false);
  const [showSaveAsNewFilterModal, setShowSaveAsNewFilterModal] = useState<boolean>(false);
  const [showSaveFilterModal, setShowSaveFilterModal] = useState<boolean>(false);
  const [editCurrentStateOnly, setEditCurrentStateOnly] = useState<boolean>(false);
  const [previousModal, setPreviousModal] = useState<string>('');
  const [isMultilineChips, setIsMultilineChips] = useState<boolean>(false);
  const [hiddenFiltersCount, setHiddenFiltersCount] = useState<number>(0);
  const tokenChipsRef = useRef<HTMLDivElement>(null);
  const setAreYouSure = useSetAreYouSure();
  const setError = useSetError();
  const isAdmin = useAppSelector(UserRedux.isAdminSelector);
  const expAnalyticsCols = useExperimentalAnalyticsCols();

  const filteredCategories = useMemo(() => {
    const categorys = R.map(
      (category: Category) => ({
        ...category,
        options: [...category.options, ...(category.isCustomSegment ? ['Unlabeled'] : [])]
      }),
      R.filter(
        (category: Category) => R.toLower(category.key) !== 'disabled',
        R.defaultTo([], categories) as Category[]
      )
    ) as CategoryWithValidity[];

    const activeDims = basic.selectedMap ? getSelectedFilterDims(basic.selectedMap) : [];
    const invalidGranularityDetails = getInvalidGranularityDetailsBySource(
      activeDims,
      tableCols,
      categorys.map(cat => cat.key || ''),
      channel || ''
    );
    const categoriesParsed = categorys.map(cat => {
      const catCopy = R.clone(cat);
      const dimName = catCopy.key || '';
      const isInvalidAnalyticsDim =
        analyticsIsSource && isInvalidAnalyticsDimension(dimName, expAnalyticsCols);
      const isInvalidGranularity = Boolean(
        invalidGranularityDetails.table[dimName] || incompatibleFilterDimsFromDelivery[dimName]
      );

      const isInvalidPeerGranularity = Boolean(invalidGranularityDetails.filter[dimName]);

      const isComputedInvalid =
        isInvalidAnalyticsDim || isInvalidGranularity || isInvalidPeerGranularity;
      if (isComputedInvalid) {
        catCopy.isInvalid = true;
        catCopy.invalidMsg = makeInvalidDimMsg({
          prettyDims: [cat.label],
          isInvalidAnalyticsDim,
          conflictingTableDims: invalidGranularityDetails.table[dimName],
          conflictingFilterDims: invalidGranularityDetails.filter[dimName],
          conflictingDeliveryDims: incompatibleFilterDimsFromDelivery[dimName],
          type: 'filter'
        });
      } else if (!cat.isInvalid) {
        catCopy.isInvalid = false;
      }
      // If cat.isInvalid came from props, R.clone already copied it along with invalidMsg.
      return catCopy;
    });
    return categoriesParsed;
  }, [
    categories,
    analyticsIsSource,
    expAnalyticsCols,
    tableCols,
    incompatibleFilterDimsFromDelivery,
    basic,
    channel
  ]);

  const filtersMap = useMemo(() => {
    const filterMap: Record<string, FilterPreset> = {};
    if (filters) {
      for (const filter of filters) {
        filterMap[filter.id] = filter;
      }
    }
    return filterMap;
  }, [filters]);

  // These are internal variables to track changes to the filter bar. When we want to propagate
  // these to the top level we will call the save function or the set state function.
  const loadState = useCallback((filterState: FilterPaneState) => {
    setIsAdvanced(filterState.isAdvanced);
    setBasic(filterState.basic);
    setAdvanced(filterState.advanced);
  }, []);

  const reset = useCallback(() => {
    loadState(state);
  }, [loadState, state]);

  // Take existing internal state and get current tokens
  const refreshFilterState = useCallback(
    filterState => {
      if (isAdvanced) {
        const tokens = filterTokenizer(advanced);
        try {
          tokensToWhereClause(tokens);
        } catch (e) {
          setCompileError(e.message);
          setError({ message: e.message, reportError: e });
        }
      }
      const refreshedFilterState = { ...filterState };
      if (filteredCategories && !R.isEmpty(filteredCategories)) {
        // Transform the filter option into a map to better access data;
        const filterMap = {};
        for (const elem of filteredCategories) {
          const elemMap = {};
          for (const entry of elem.options) {
            if (entry) {
              if (typeof entry === 'string') {
                elemMap[entry] = true;
              } else {
                if (entry.label) {
                  elemMap[entry.label] = true;
                }
                if (entry.value) {
                  elemMap[entry.value] = true;
                }
              }
            }
          }
          filterMap[elem.key.toLowerCase()] = elemMap;
        }
        if (refreshedFilterState.basic) {
          for (const map of R.keys(refreshedFilterState.basic)) {
            for (const category of R.keys(refreshedFilterState.basic[map])) {
              for (const key of R.keys(refreshedFilterState.basic[map][category])) {
                if (!filterMap[(category as string).toLowerCase()][key]) {
                  delete refreshedFilterState.basic[map][category][key];
                }
              }
            }
          }
        }
        if (refreshedFilterState.advanced) {
          for (const map of R.keys(refreshedFilterState.advanced)) {
            for (const category of R.keys(refreshedFilterState.advanced[map])) {
              for (const key of R.keys(refreshedFilterState.advanced[map][category])) {
                if (!filterMap[(category as string).toLowerCase()][key]) {
                  delete refreshedFilterState.advanced[map][category][key];
                }
              }
            }
          }
        }
      }
      return refreshedFilterState;
    },
    [advanced, filteredCategories, isAdvanced, setError]
  );

  const clear = useCallback(() => {
    setBasic({
      notMap: {},
      selectedMap: {}
    });
    setAdvanced('');
  }, []);

  // Fully clears the applied filter, including which preset is selected — not just the visible
  // basic/advanced selections. Without resetting filterID/selectedFilter/filterSource too, the page
  // fetch (which reads filterID off filterSource === 'currentFilterID') keeps using the preset that
  // was supposedly cleared.
  const clearFilter = useCallback(() => {
    setFilterSource('currentState');
    setFilterID(undefined);
    setSelectedFilter(undefined);
    setState(DEFAULT_FILTER_PANE_STATE);
    loadState(DEFAULT_FILTER_PANE_STATE);
    clear();
  }, [clear, loadState, setFilterID, setFilterSource, setState]);

  const confirmSave = useCallback(
    (onOkay: () => void) => {
      setAreYouSure({
        title: 'Save Preset',
        message: 'Saving this preset cannot be undone. Are you sure?',
        okayText: 'Save',
        cancelText: 'Cancel',
        intent: Intent.WARNING,
        onOkay
      });
    },
    [setAreYouSure]
  );

  const closeModals = useCallback(
    (revertToPreviousSelectedFilter = true) => {
      if (selectedFilter) {
        setAdminLock(R.defaultTo(false, selectedFilter.admin_lock));
      }
      setNewFilterName(undefined);
      setPreviousModal('');
      if (revertToPreviousSelectedFilter) {
        setSelectedFilter(filtersMap[`${filterID}`]);
        loadState(state);
      }
      setShowCreateFilterModal(false);
      setShowEditFilterModal(false);
      setShowRenameFilterModal(false);
      setShowSaveAsNewFilterModal(false);
      setShowSaveFilterModal(false);
      setEditCurrentStateOnly(false);
    },
    [selectedFilter, filterID, filtersMap, loadState, state]
  );

  const getFilters = useCallback(
    (filterIDToSet?: number) => {
      (async () => {
        try {
          setFetchingFilters(true);
          if (company && platform) {
            const params = {
              company,
              platform
            };
            const res = await FiltersLambdaFetch<GetFilterPresetsParams>('/getFilters', {
              params
            });
            const parsedFilterPresets = await awaitJSON(res);
            const fetchedFilters: FilterPreset[] = parsedFilterPresets.length
              ? parsedFilterPresets
              : [];
            setFilters(fetchedFilters);
            if (R.defaultTo(filterID, filterIDToSet)) {
              for (const filter of fetchedFilters) {
                if (R.defaultTo(filterID, filterIDToSet) === filter.id) {
                  setSelectedFilter(filter);
                  setAdminLock(R.defaultTo(false, filter.admin_lock));
                  if (filter.filter_state) {
                    setState(filter.filter_state);
                    setAdvanced(filter.filter_state.advanced);
                    setBasic(filter.filter_state.basic);
                    setIsAdvanced(filter.isAdvanced);
                  }
                }
              }
            }
          }
        } catch (e) {
          setError({
            reportError: e,
            message: `Failed to fetch filter presets. Error: ${e.message}`
          });
        } finally {
          setFetchingFilters(false);
        }
      })();
    },
    [company, filterID, platform, setError, setFilters, setState]
  );

  const saveFilter = useCallback(
    ({
      filter,
      refreshView,
      saveOnly
    }: {
      filter: Partial<FilterPreset>;
      refreshView?: boolean;
      saveOnly?: boolean;
    }) => {
      (async () => {
        // Update latest state we have setup in the filter bar
        const newFilterState = refreshFilterState(filter.filter_state);
        const newFilterText = isAdvanced
          ? newFilterState.advanced
          : buildSqlBasicToAdvanced(newFilterState.basic);
        const newFilterTokens = filterTokenizer(newFilterText);
        const newWhereClause = tokensToWhereClause(newFilterTokens);
        const newWhereClauseSize = getPayloadSize(newWhereClause);
        if (newWhereClauseSize > MAX_WHERE_CLAUSE_SIZE) {
          setError({
            message: `The filter created is too large (${
              newWhereClauseSize / 1000.0
            }kB). If you are using the basic filter pane, please switch to the advanced filter pane.`
          });
          return;
        }
        // Build the payload
        const filterToBeSaved: Partial<FilterPreset> = {
          company,
          filter_state: newFilterState,
          name: filter.name,
          isAdvanced,
          platform,
          admin_lock: R.isNil(filter.admin_lock) ? adminLock : filter.admin_lock
        };
        if (!R.isNil(filter.id)) {
          filterToBeSaved.id = filter.id;
        }
        const nameError = getFilterNameError(filterToBeSaved.name);
        if (nameError) {
          setError({ message: nameError });
          return;
        }
        try {
          const body = {
            filter: filterToBeSaved as FilterPreset
          };
          const res = await FiltersLambdaFetch<SaveFilterPresetBody>('/saveFilter', {
            method: 'POST',
            body
          });
          setFetchingFilters(true);
          const savedFilter: FilterPreset = await awaitJSON(res);
          getFilters(saveOnly ? undefined : savedFilter.id); // Need this line since setFilterID won't take affect before we reset states from this function
          if (refreshView && !saveOnly) {
            refreshData();
          }
          if (!saveOnly) {
            setFilterID(savedFilter.id);
            setSelectedFilter(savedFilter);
            setState(newFilterState);
            setNewFilterName(savedFilter.name);
          }
        } catch (e) {
          setCompileError(e.message);
          setError({
            reportError: e,
            message: `Failed to save filter preset. Error: ${e.message}`
          });
        } finally {
          closeModals();
        }
      })();
    },
    [
      adminLock,
      closeModals,
      company,
      getFilters,
      isAdvanced,
      platform,
      refreshData,
      refreshFilterState,
      setError,
      setFilterID,
      setState
    ]
  );

  const deleteFilter = useCallback(
    (id: number) => {
      setAreYouSure({
        title: 'Delete Filter Preset',
        message: 'You are about to delete this filter preset, are you sure?',
        intent: Intent.ERROR,
        cancelText: 'Never mind',
        okayText: 'Yes',
        onOkay: async () => {
          if (id) {
            setFetchingFilters(true);
            const params: DeleteFilterPresetParams = {
              id
            };
            try {
              await FiltersLambdaFetch<DeleteFilterPresetParams>('/deleteFilter', {
                method: 'DELETE',
                params
              });
            } catch (e) {
              setError({
                reportError: e,
                message: `Failed to save filter preset. Error: ${e.message}`
              });
            } finally {
              getFilters();
            }
          }
          if (selectedFilter && selectedFilter.id === id) {
            setSelectedFilter(undefined);
            setFilterID(undefined);
            setState(DEFAULT_FILTER_PANE_STATE);
            clear();
          }
        }
      });
    },
    [clear, getFilters, selectedFilter, setAreYouSure, setError, setFilterID, setState]
  );

  useEffect(() => {
    reset();
  }, [state, reset]);

  useEffect(() => {
    if (R.isNil(filters)) {
      getFilters();
    }
  }, [filters, getFilters]);

  useEffect(() => {
    if (R.isNil(selectedFilter)) {
      setSaveAsNew(true);
    }
  }, [selectedFilter]);

  // Here we only want to update the filter when filterID changes as an input from the page
  // make sure we don't update this when we are editing (modals are active)
  useEffect(() => {
    if (
      !showCreateFilterModal &&
      !showEditFilterModal &&
      !showRenameFilterModal &&
      !showSaveAsNewFilterModal &&
      !showSaveFilterModal &&
      selectedFilter &&
      selectedFilter.id !== filterID &&
      (filterSource === 'presetFilterID' || filterSource === 'currentFilterID')
    ) {
      const filter = R.find(f => f.id === filterID, R.defaultTo([], filters) as FilterPreset[]);
      if (filter) {
        setSelectedFilter(filter);
        setFilterID(filter.id);
        setState(filter.filter_state);
        loadState(filter.filter_state);
      }
    }
    // Reset selected filter when filterID becomes undefined, unless a modal is open
    // (e.g. three-dot Edit temporarily sets selectedFilter to the preset being edited)
    if (
      R.isNil(filterID) &&
      selectedFilter &&
      !showCreateFilterModal &&
      !showEditFilterModal &&
      !showRenameFilterModal &&
      !showSaveAsNewFilterModal &&
      !showSaveFilterModal
    ) {
      setSelectedFilter(undefined);
    }
  }, [
    filterID,
    filters,
    filterSource,
    loadState,
    selectedFilter,
    setFilterID,
    setState,
    showCreateFilterModal,
    showEditFilterModal,
    showRenameFilterModal,
    showSaveAsNewFilterModal,
    showSaveFilterModal
  ]);

  const saveDisabled = useMemo(
    () => (state.isAdvanced ? !state.advanced.length : !R.keys(state.basic.selectedMap).length),
    [state]
  );

  const isEdited = useMemo(() => {
    if (selectedFilter && selectedFilter.filter_state) {
      return !areFilterStatesEqual(selectedFilter.filter_state, state);
    }
    return false;
  }, [selectedFilter, state]);

  // React Components
  const presetDropdown = useMemo(
    () =>
      simpleFilterStateOnly ? null : (
        <div
          className={cn('preset-dropdown', {
            'preset-selected': filterID !== undefined && Boolean(filtersMap[filterID])
          })}
        >
          <PresetDropdown
            addPresetOnClick={() => {
              setSelectedFilter({
                id: -1,
                name: 'My New Preset 1',
                filter_state: {
                  advanced: '',
                  basic: {
                    notMap: {},
                    selectedMap: {}
                  },
                  isAdvanced: false
                },
                company: '',
                filter_tokens: [],
                isAdvanced: false,
                lastmodified: '',
                lastuser: '',
                created_at: '',
                created_by: '',
                admin_lock: false
              });
              setNewFilterName('My New Preset 1');
              setShowCreateFilterModal(true);
            }}
            addPresetText='Create New Preset'
            allowNoSelections
            applyOnClick={preset => {
              if (preset) {
                setFilterID(preset.id);
                setSelectedFilter(preset);
                setState(preset.filter_state);
                loadState(preset.filter_state);
                setFilterSource('currentFilterID');
              } else {
                clearFilter();
              }
            }}
            cancelOnClick={() => {}}
            className={cn({ loading: fetchingFilters })}
            deleteItemOnClick={presetID => deleteFilter(presetID)}
            duplicateItemOnClick={preset =>
              confirmSave(() =>
                saveFilter({
                  filter: {
                    ...(R.omit(['id'], preset) as FilterPreset),
                    name: `${preset.name} (Copy)`,
                    admin_lock: false
                  },
                  refreshView: false,
                  saveOnly: true
                })
              )
            }
            editItemOnClick={preset => {
              setSelectedFilter(preset);
              setAdminLock(preset.admin_lock);
              loadState(preset.filter_state);
              setEditCurrentStateOnly(false);
              setShowEditFilterModal(true);
            }}
            hasTicks
            label={
              filtersMap && filterID !== undefined && filtersMap[filterID]
                ? `${filtersMap[filterID].name}${isEdited ? ' [edited]' : ''}`
                : 'Load Filter Preset'
            }
            presets={R.sortBy(R.prop('name'), R.defaultTo([], filters) as FilterPreset[])}
            renameItemOnClick={preset => {
              setSelectedFilter(filtersMap[preset.id]);
              setAdminLock(preset.admin_lock);
              loadState(filtersMap[preset.id].filter_state);
              setShowRenameFilterModal(true);
            }}
            selectedPreset={
              filtersMap && filterID !== undefined && filtersMap[filterID]
                ? filtersMap[filterID]
                : undefined
            }
            analyticsIsSource={analyticsIsSource}
            presetType='filter'
            otherPresetCols={tableCols}
            channel={channel}
            invalidGranularityDetailsFromDeliveryDim={incompatibleFilterDimsFromDelivery}
          />
        </div>
      ),
    [
      clearFilter,
      confirmSave,
      deleteFilter,
      fetchingFilters,
      filterID,
      filters,
      filtersMap,
      isEdited,
      loadState,
      saveFilter,
      setFilterID,
      setFilterSource,
      setState,
      analyticsIsSource,
      simpleFilterStateOnly,
      tableCols,
      channel,
      incompatibleFilterDimsFromDelivery
    ]
  );

  // Check if a filter/category has any selected items
  const hasSelectedItems = useCallback(
    (category: CategoryWithValidity, filterState: FilterPaneState) => {
      const selectedMap = filterState.basic.selectedMap || {};
      const catMap = selectedMap[category.key] || {};
      return Object.values(catMap).some(Boolean);
    },
    []
  );

  // Check if any filters are selected across all categories or advanced filters
  const anyFiltersSelected = useMemo(
    () =>
      Object.values(state.basic.selectedMap || {}).some(catMap =>
        Object.values(catMap).some(Boolean)
      ) ||
      (state.advanced && state.advanced.length > 0),
    [state.basic.selectedMap, state.advanced]
  );

  const usedCategories = filteredCategories.filter(cat => hasSelectedItems(cat, state));

  // Check if any grouped segments are being used
  const usedSegmentGroups = useMemo(() => {
    const groups: string[] = [];
    if (!groupedCategories || !state.basic?.selectedMap) {
      return [];
    }
    const segmentGroupNames = Object.keys(groupedCategories);

    for (const groupName of segmentGroupNames) {
      for (const segment of groupedCategories[groupName]) {
        const catMap = state.basic.selectedMap[segment.segmentName];
        if (catMap && catMap.Yes === true && !groups.includes(groupName)) {
          groups.push(groupName);
        }
      }
    }
    return groups;
  }, [groupedCategories, state.basic.selectedMap]);

  const groupedSegmentNames = Object.values(groupedCategories).flatMap(gc =>
    gc.map(c => c.segmentName)
  );
  const filteredUsedCategories = [
    ...usedCategories.filter(cat => !groupedSegmentNames.includes(cat.key)),
    ...usedSegmentGroups.map(groupName => ({
      label: groupName,
      key: groupName,
      options: groupedCategories[groupName].map(gc => gc.segmentName),
      isCustomSegment: true
    }))
  ];

  // Check if chips span multiple rows and count hidden filters after DOM updates
  useLayoutEffect(() => {
    const checkMultilineChips = () => {
      // When expanded, CSS removes max-height so scrollHeight === offsetHeight — skip recalculating
      // to keep isMultilineChips true so the "Show less" button stays visible.
      if (expanded) return;
      const tokenChipsReference = tokenChipsRef.current;
      if (tokenChipsReference) {
        const isMultiline = tokenChipsReference.scrollHeight > tokenChipsReference.offsetHeight;
        setIsMultilineChips(isMultiline);

        if (isMultiline) {
          const chipElements = tokenChipsReference.querySelectorAll('.token-chip');
          if (chipElements.length > 0) {
            const firstRowTop = (chipElements[0] as HTMLElement).offsetTop;
            let visibleCount = 0;
            chipElements.forEach(chip => {
              if ((chip as HTMLElement).offsetTop === firstRowTop) {
                visibleCount++;
              }
            });
            setHiddenFiltersCount(Math.max(0, chipElements.length - visibleCount));
          }
        } else {
          setHiddenFiltersCount(0);
        }
      }
    };

    // Check immediately and after a short delay to ensure DOM is updated
    checkMultilineChips();
    const timeoutId = setTimeout(checkMultilineChips, 0);

    return () => clearTimeout(timeoutId);
  }, [state, expanded]);

  let addEditFiltersLabel = 'Add Filters';
  if (anyFiltersSelected) {
    addEditFiltersLabel = isAdmin || !adminLock ? 'Edit Filters' : 'View Filters';
  }

  const addEditFiltersChip = (
    <div className='token-chip'>
      <Button
        size={Size.LG}
        appearance={Appearance.OUTLINED}
        className='token-chip-button add-edit-filters-button'
        leadingElement={anyFiltersSelected ? <MdEdit /> : <MdAdd />}
        onClick={() => {
          setEditCurrentStateOnly(Boolean(anyFiltersSelected));
          setShowEditFilterModal(true);
          setAdminLock(
            selectedFilter && selectedFilter.admin_lock ? selectedFilter.admin_lock : false
          );
          if (selectedFilter) {
            setNewFilterName(R.defaultTo('My New Preset 1', selectedFilter.name));
          }
        }}
      >
        {addEditFiltersLabel}
      </Button>
    </div>
  );

  const controls = (
    <div className='controls'>
      {isMultilineChips && (hiddenFiltersCount > 0 || expanded) && (
        <Button
          size={Size.LG}
          className='expand-button'
          trailingElement={expanded ? <MdOutlineArrowBackIos /> : <MdOutlineArrowForwardIos />}
          onClick={() => setExpanded(!expanded)}
          appearance={Appearance.EMPTY}
        >
          {hiddenFiltersCount > 0 && !expanded ? `+${hiddenFiltersCount} more` : 'Show less'}
        </Button>
      )}
      {isMultilineChips && addEditFiltersChip}
      {anyFiltersSelected && (
        <IconButton
          className='clear-container'
          appearance={Appearance.EMPTY}
          onClick={() => {
            clearFilter();
            setExpanded(false);
          }}
        >
          <MdClear />
        </IconButton>
      )}

      {anyFiltersSelected && !simpleFilterStateOnly && (
        <IconButton
          appearance={Appearance.EMPTY}
          className={cn('save-container', {
            disabled: saveDisabled || Boolean(selectedFilter && !isEdited)
          })}
          disabled={saveDisabled || Boolean(selectedFilter && !isEdited)}
          tooltip={{ text: 'Save', placement: Placement.BOTTOM }}
          onClick={() => {
            if (selectedFilter) {
              setShowSaveFilterModal(true);
            } else {
              setShowSaveAsNewFilterModal(true);
            }
          }}
        >
          <MdSave />
        </IconButton>
      )}
    </div>
  );

  const tokenChips = (
    <div className='token-chips' ref={tokenChipsRef}>
      {expanded && presetDropdown}
      {state.isAdvanced ? (
        <FetchFilterBarChip
          key='advancedFetchFilterBarChip'
          advanced={state.advanced}
          expanded={expanded}
          setInitialOpenSection={setInitialOpenSection}
          setShowEditFilterModal={setShowEditFilterModal}
          containerRef={tokenChipsRef}
        />
      ) : (
        R.map(
          category => (
            <FetchFilterBarChip
              key={`basicFetchFilterBarChip${category.label}`}
              category={category}
              expanded={expanded}
              filterState={state}
              setInitialOpenSection={setInitialOpenSection}
              setShowEditFilterModal={setShowEditFilterModal}
              containerRef={tokenChipsRef}
              groupNames={Object.keys(groupedCategories)}
            />
          ),
          sortCategories(filteredUsedCategories as Category[], state)
        )
      )}
      {!isMultilineChips && addEditFiltersChip}
      {expanded && <div className='embedded-controls'>{controls}</div>}
    </div>
  );

  const saveModalOverwriteOption = selectedFilter && (
    <Button
      size={Size.LG}
      className='save-option-container'
      appearance={Appearance.EMPTY}
      onClick={() => {
        setSaveAsNew(false);
        setNewFilterName(undefined);
      }}
    >
      <div className='save-option'>
        <div className='save-option-tick'>{saveAsNew ? <MdOutlineCircle /> : <MdCircle />}</div>
        <div className='save-option-text'>{`Save ${selectedFilter.name}`}</div>
      </div>
    </Button>
  );

  const saveModalAsNewOption = (
    <Button
      size={Size.LG}
      className='save-option-container'
      appearance={Appearance.EMPTY}
      onClick={() => {
        setSaveAsNew(true);
      }}
    >
      <div
        className={cn('save-option', {
          'only-option': showRenameFilterModal || showSaveAsNewFilterModal
        })}
      >
        <div
          className={cn('save-option-tick', {
            transparent: showRenameFilterModal || showSaveAsNewFilterModal
          })}
        >
          {saveAsNew ? <MdCircle /> : <MdOutlineCircle />}
        </div>
        <div className='save-option-text'>Save as New Preset</div>
      </div>
      <div className='save-input-container'>
        <Form.Control
          className='save-input'
          onChange={e => setNewFilterName(e.target.value)}
          placeholder='Enter new preset name'
          value={newFilterName}
        />
      </div>
    </Button>
  );

  const saveModal = showSaveFilterModal && (
    <Modal show centered size='lg' onHide={() => closeModals()} className='save-modal'>
      <Modal.Header>
        <Modal.Title className='save-header'>Save Preset</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {saveModalOverwriteOption}
        {saveModalAsNewOption}
      </Modal.Body>
      <Modal.Footer>
        <Button
          className='cancel-button'
          design={Design.NEUTRAL}
          onClick={() => closeModals()}
          appearance={Appearance.OUTLINED}
        >
          {showRenameFilterModal ? 'Discard' : 'Cancel'}
        </Button>
        <Button
          className='apply-button'
          disabled={adminLock && !isAdmin}
          onClick={() => {
            confirmSave(() => {
              saveFilter({
                filter: buildSavePresetFilter({
                  selectedFilter,
                  saveAsNew,
                  company,
                  state,
                  newFilterName
                }),
                refreshView: false,
                saveOnly: true
              });
            });
          }}
        >
          Save
        </Button>
      </Modal.Footer>
    </Modal>
  );

  let renameSaveAsNewModalTitle = 'Save Preset';
  if (showSaveAsNewFilterModal) {
    renameSaveAsNewModalTitle = 'Save as New Preset';
  } else if (showRenameFilterModal) {
    renameSaveAsNewModalTitle = `Rename ${selectedFilter?.name ?? 'Preset'}`;
  }

  const renameSaveAsNewModal = (showRenameFilterModal || showSaveAsNewFilterModal) && (
    <RenameModal
      applyOnClick={newName => {
        confirmSave(() => {
          if (showSaveAsNewFilterModal && previousModal === 'editFilters') {
            setFilterSource('presetFilterID');
          }
          saveFilter({
            filter: showSaveAsNewFilterModal
              ? {
                  company,
                  filter_state: { advanced, basic, isAdvanced },
                  filter_tokens: [],
                  isAdvanced,
                  name: newName
                }
              : {
                  ...selectedFilter,
                  name: newName
                },
            refreshView: showSaveAsNewFilterModal,
            saveOnly: showSaveAsNewFilterModal
          });
        });
      }}
      applyText={
        showSaveAsNewFilterModal && previousModal === 'editFilters' ? 'Save and Apply' : 'Save'
      }
      cancelText={showRenameFilterModal ? 'Discard' : 'Cancel'}
      disabled={adminLock && !isAdmin}
      onCancel={() => {
        if (showSaveAsNewFilterModal && previousModal === 'editFilters') {
          setShowSaveAsNewFilterModal(false);
          setShowEditFilterModal(true);
        } else {
          closeModals();
        }
      }}
      onHide={() => closeModals()}
      placeholder='Enter new preset name'
      subTitle={
        showRenameFilterModal || showSaveAsNewFilterModal ? 'New Preset Name' : 'Save as New Preset'
      }
      title={renameSaveAsNewModalTitle}
    />
  );

  let applyButtonLabel = 'Save as New Preset';
  if (showCreateFilterModal) {
    applyButtonLabel = 'Save as Preset';
  } else if (showEditFilterModal && !R.isNil(selectedFilter) && !editCurrentStateOnly) {
    applyButtonLabel = 'Apply';
  }

  const editFilterTitleContent = (() => {
    if (showEditFilterModal && (R.isNil(selectedFilter) || editCurrentStateOnly)) {
      return <Modal.Title>Edit Filters</Modal.Title>;
    }
    if (renameFilterOnSave) {
      return (
        <Form.Control
          className='save-input'
          onChange={e => setNewFilterName(e.target.value)}
          value={newFilterName}
        />
      );
    }
    const titleName =
      selectedFilter &&
      ((showEditFilterModal && !R.isNil(selectedFilter) && !editCurrentStateOnly) ||
        showCreateFilterModal)
        ? selectedFilter.name
        : newFilterName;
    return <Modal.Title>{titleName}</Modal.Title>;
  })();

  const editFilterModal = (showEditFilterModal || showCreateFilterModal) && (
    <Modal show centered size='lg' onHide={() => closeModals()} className='edit-modal'>
      <Modal.Header>
        <div className='filter-title-container'>
          {((showEditFilterModal && !R.isNil(selectedFilter) && !editCurrentStateOnly) ||
            showCreateFilterModal) && (
            <div className='modal-type'>
              {showEditFilterModal ? 'Edit Preset' : 'Create New Preset'}
            </div>
          )}
          <div className='filter-title'>
            {editFilterTitleContent}
            {((showEditFilterModal && !R.isNil(selectedFilter) && !editCurrentStateOnly) ||
              showCreateFilterModal) && (
              <Button
                size={Size.LG}
                className='edit-icon-container'
                appearance={Appearance.EMPTY}
                onClick={() => setRenameFilterOnSave(!renameFilterOnSave)}
              >
                {renameFilterOnSave ? <MdCancel /> : <MdEdit />}
              </Button>
            )}
          </div>
        </div>
        {simpleFilterStateOnly ? null : (
          <div className='buttons-container'>
            {!editCurrentStateOnly && (
              <AdminLockToggle
                checked={adminLock}
                onChange={() => setAdminLock(!adminLock)}
                disabled={!isAdmin}
                background={Background.LIGHT}
              />
            )}
            <SegmentedControlButton
              size={Size.LG}
              options={['Basic', 'Advanced']}
              selectedOption={isAdvanced ? 'Advanced' : 'Basic'}
              onChange={option => setIsAdvanced(option === 'Advanced')}
            />
          </div>
        )}
      </Modal.Header>
      <Modal.Body>
        {isAdvanced ? (
          <FilterPaneAdvanced
            categories={filteredCategories}
            state={advanced}
            setState={setAdvanced}
            compileError={compileError}
            readonly={readonly}
            setEditing={() => {}}
            analyticsIsSource={analyticsIsSource}
            tableCols={tableCols}
            incompatibleFilterDimsFromDelivery={incompatibleFilterDimsFromDelivery}
            channel={channel}
          />
        ) : (
          <FilterPaneBasic
            categories={filteredCategories}
            state={basic}
            initialOpenSection={
              initialOpenSection || (filteredCategories[0] ? filteredCategories[0].key : '')
            }
            setState={setBasic}
            readonly={readonly}
            groupedCategories={groupedCategories}
          />
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button
          className='cancel-button'
          design={Design.NEUTRAL}
          onClick={() => closeModals()}
          appearance={Appearance.OUTLINED}
        >
          Cancel
        </Button>
        {simpleFilterStateOnly ? null : (
          <Button
            className='apply-button'
            onClick={() => {
              if (showCreateFilterModal) {
                confirmSave(() => {
                  saveFilter({
                    filter: {
                      company,
                      filter_state: { advanced, basic, isAdvanced },
                      filter_tokens: [],
                      isAdvanced,
                      name:
                        newFilterName || (selectedFilter ? selectedFilter.name : 'My New Preset 1')
                    },
                    saveOnly: true
                  });
                  closeModals(false);
                });
              } else if (showEditFilterModal && !R.isNil(selectedFilter) && !editCurrentStateOnly) {
                setFilterSource('currentState');
                setState(refreshFilterState({ advanced, basic, isAdvanced }));
                if (selectedFilter && selectedFilter.id) {
                  setFilterID(selectedFilter.id);
                }
                refreshData();
                closeModals();
              } else {
                setPreviousModal('editFilters');
                setShowEditFilterModal(false);
                setShowCreateFilterModal(false);
                setShowSaveAsNewFilterModal(true);
              }
            }}
          >
            {applyButtonLabel}
          </Button>
        )}
        <Button
          className='save-and-apply-button'
          disabled={adminLock && !isAdmin && !editCurrentStateOnly}
          onClick={() => {
            if (showCreateFilterModal) {
              confirmSave(() => {
                setFilterSource('currentFilterID');
                saveFilter({
                  filter: {
                    company,
                    filter_state: { advanced, basic, isAdvanced },
                    filter_tokens: [],
                    isAdvanced,
                    name: newFilterName
                  },
                  refreshView: true
                });
              });
            } else if (editCurrentStateOnly && selectedFilter) {
              setFilterSource('currentState');
              setState(refreshFilterState({ advanced, basic, isAdvanced }));
              setFilterID(selectedFilter.id);
              refreshData();
              closeModals();
            } else if (showEditFilterModal && selectedFilter) {
              confirmSave(() => {
                setFilterSource('currentFilterID');
                saveFilter({
                  filter: {
                    ...selectedFilter,
                    filter_state: { advanced, basic, isAdvanced },
                    name: !R.isNil(newFilterName) ? newFilterName : selectedFilter.name
                  },
                  refreshView: true
                });
              });
            } else {
              setFilterSource('currentState');
              const refreshedFilterState = refreshFilterState({ advanced, basic, isAdvanced });
              loadState(refreshedFilterState);
              setState(refreshedFilterState);
              setFilterID(undefined);
              refreshData();
              closeModals();
            }
          }}
        >
          {showCreateFilterModal ||
          (showEditFilterModal && !R.isNil(selectedFilter) && !editCurrentStateOnly)
            ? 'Save and Apply'
            : 'Apply'}
        </Button>
      </Modal.Footer>
    </Modal>
  );

  return (
    <div className={cn('fetch-filter-bar', { disabled, expanded }, className)}>
      {renameSaveAsNewModal}
      {saveModal}
      {editFilterModal}
      {!expanded && presetDropdown}
      {tokenChips}
      {!expanded && controls}
    </div>
  );
};
